import { IPremadeQuiz } from '@quizzach/interfaces';
import { consoleDev, ConsoleStatus, fetchData } from '@quizzach/utils';
import { useState } from 'react';

import { generateMockAuthorSales } from './authorAnalytics.mock';
import { MOCK_AUTHOR_ANALYTICS, MOCK_AUTHOR_SLUG } from './mocks.config';

export interface AuthorSalesData {
	created_at: string;
	price: string;
	premade_quiz: {
		uuid: string;
		title: string;
		cover_photo: string;
		cover_photo_small: string;
		author: {
			display_name: string;
			display_name_slug: string;
		};
	};
	quiz_type: string;
}

export const useAuthorAnalytics = () => {
	const [authorAnalytics, setAuthorAnalytics] = useState<AuthorSalesData[]>();
	const [error, setError] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [uuidOccurrences, setUuidOccurrences] = useState<Map<string, number>>(
		new Map()
	);

	const countUuidOccurrences = (
		data: AuthorSalesData[]
	): Map<string, number> => {
		const uuidMap = new Map<string, number>();
		for (const item of data) {
			const uuid = item.premade_quiz.uuid;
			uuidMap.set(uuid, (uuidMap.get(uuid) || 0) + 1);
		}
		return uuidMap;
	};

	const getAuthorStatistics = async (authorSlug?: string) => {
		setLoading(true);
		try {
			const response = MOCK_AUTHOR_ANALYTICS
				? await fetchData<IPremadeQuiz[]>(
						`/api/premade-quizzes?author.display_name_slug=${
							MOCK_AUTHOR_SLUG || authorSlug || ''
						}`,
						{ method: 'GET' }
					)
				: await fetchData<any>(
						`/api/author-analytics`,
						{
							method: 'GET',
						},
						{
							authRoute: true,
						}
					);
			if (response.status >= 300) {
				consoleDev('author-analytics', ConsoleStatus.HTTP, response.status);
				setError(true);
			} else {
				consoleDev('author-analytics', ConsoleStatus.SUCC);
				setError(false);

				// The real API serializes created_at as an ISO 8601 string, the
				// mock as unix seconds; downstream (sort below, convertUnixTimestamp
				// in the view) expects unix seconds, so normalize here.
				const toUnixSeconds = (value: string) =>
					/^\d+$/.test(value)
						? value
						: String(Math.floor(new Date(value).getTime() / 1000));

				const rawData: AuthorSalesData[] = MOCK_AUTHOR_ANALYTICS
					? generateMockAuthorSales(response.data as IPremadeQuiz[])
					: response.data;

				const data = rawData.map((item) => ({
					...item,
					created_at: toUnixSeconds(item.created_at),
				}));

				const sortedData = data.sort(
					(a: AuthorSalesData, b: AuthorSalesData) =>
						+b.created_at - +a.created_at
				);

				setAuthorAnalytics(sortedData);
				setUuidOccurrences(countUuidOccurrences(sortedData));
			}
		} catch (err) {
			consoleDev('author-analytics', ConsoleStatus.NET, err);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	return {
		authorAnalytics,
		error,
		loading,
		uuidOccurrences,
		getAuthorStatistics,
	};
};
